/**
 * External dependencies
 */
import React, { memo, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Modal from '@naruto/naruto-components/src/base/v1/Modal';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

/**
 * Internal dependencies
 */
import { i18ns } from '@naruto/naruto-models/src/core/i18n';
import Lang from './lang';
import { ModalDialogProps, DialogItemObject } from './types';

function DialogItem(props: DialogItemObject): React.ReactElement {
  /* Global & Local States */
  const { dialog, removeDialog } = props;
  const [hiddenDebugMessage, setHiddenDebugMessage] = useState(false);
  const {
    typeTitle = '',
    typeContent = '',
    typeHiddenMessageTitle = '',
    typeHiddenMessage = '',
    confirmText = '',
    showHiddenMessage = true,
    confirmTextVariation,
    confirmTextColor,
    onConfirm
  } = dialog;

  /* Functions */
  const onToggleHiddenDebugMessage = (): void => setHiddenDebugMessage(prevIsHidden => !prevIsHidden);
  const onConfirmClick = (): void => {
    if (onConfirm) onConfirm();
    removeDialog();
  };

  /* Views */
  return (
    <Box
      sx={{
        width: '100%',
        position: 'relative'
      }}
    >
      <Box
        sx={{
          width: '100%',
          marginBottom: '8px',
          position: 'relative',
          fontWeight: 'bold !important',
          display: 'flex',
          alignItems: 'center'
        }}
        className="test-dialogItemTitle"
      >
        <Typography sx={{ lineHeight: '36px !important' }} variant="h4" color="textPrimary">
          {typeTitle}
        </Typography>
      </Box>
      <Box
        sx={{
          width: '100%',
          minHeight: '24px',
          marginBottom: '24px',
          position: 'relative'
        }}
      >
        <Typography variant="body1" color="textPrimary" style={{ whiteSpace: 'pre-wrap' }}>
          {typeContent}
        </Typography>
      </Box>
      <Box
        sx={{
          width: '100%',
          minHeight: '24px',
          display: 'flex',
          justifyContent: 'flex-end',
          position: 'relative'
        }}
      >
        {showHiddenMessage && !!typeHiddenMessage && (
          <Button
            sx={{
              left: '-2px',
              bottom: '-2px',
              position: 'absolute',
              background: '#F0F4F9'
            }}
            variant="text"
            color="primary"
            onClick={onToggleHiddenDebugMessage}
          >
            {hiddenDebugMessage ? i18ns.__(Lang.DEBUG_CLOSE_BUTTON) : i18ns.__(Lang.DEBUG_OPEN_BUTTON)}
            {hiddenDebugMessage ? (
              <KeyboardArrowUpIcon fontSize="small" sx={{ ml: 2 }} />
            ) : (
              <KeyboardArrowDownIcon fontSize="small" sx={{ ml: 2 }} />
            )}
          </Button>
        )}
        <Button
          className="test-dialogItemActionButton"
          variant={!confirmTextVariation ? 'contained' : confirmTextVariation}
          color={!confirmTextColor ? 'primary' : confirmTextColor}
          onClick={onConfirmClick}
        >
          {confirmText || i18ns.__(Lang.CONFIRM_BUTTON)}
        </Button>
      </Box>
      {showHiddenMessage && (
        <>
          {hiddenDebugMessage && !!typeHiddenMessage && (
            <Box sx={{ mt: 3 }}>
              <Typography
                variant="body1"
                color="textPrimary"
                sx={{ whiteSpace: 'pre-wrap', mb: 2, fontWeight: 'bold' }}
              >
                {typeHiddenMessageTitle}
              </Typography>
              <Box
                sx={{
                  width: '100%',
                  minHeight: '24px',
                  maxHeight: '368px',
                  marginBottom: '18px',
                  position: 'relative',
                  padding: '16px',
                  background: '#F5F5F5',
                  wordBreak: 'break-word',
                  overflow: 'auto'
                }}
              >
                <Typography variant="body2" color="textPrimary" style={{ whiteSpace: 'pre-wrap' }}>
                  {typeHiddenMessage}
                </Typography>
              </Box>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

function ModalDialog(props: ModalDialogProps): React.ReactElement {
  /* Global & Local States */
  const { list, removeDialog } = props;

  /* Data */
  const disableEscape = useMemo(() => {
    if (list.length > 0) {
      return Boolean(list[0].typeDisableEscape);
    }
    return false;
  }, [list]);

  /* Views */
  const renderMessages = useMemo(() => {
    if (list.length > 0) {
      return <DialogItem dialog={list[0]} removeDialog={removeDialog} />;
    }
    return <React.Fragment />;
  }, [list]);
  const renderIsMessageOpen = useMemo(() => {
    if (list.length > 0) {
      return true;
    }
    return false;
  }, [list]);

  /* Main */
  return (
    <Modal
      open={renderIsMessageOpen}
      onClose={removeDialog}
      typeSize="sm"
      typeTheme="custom"
      disableCancelButton
      disableConfirmButton
      disableEscapeKeyDown={disableEscape}
      disableBackdropClick={disableEscape}
      closeIcon={!disableEscape}
    >
      {renderMessages}
    </Modal>
  );
}

export default memo(ModalDialog);
